import { useTranslation } from 'react-i18next';
import { SETTINGS_SECTION_SPECS } from '@/pages/settings/sections';
import { NAV_SECTIONS, type NavLeaf } from './nav-config';
import { useActiveNav, matchesPath } from './use-nav-state';

export interface Crumb {
  label: string;
  href?: string;
}

/**
 * Derives the page trail from the nav config.
 *
 * Replaces the old shell's ~35-line IIFE of nested `if
 * (pathname.startsWith(...)) return t(...)` branches, which listed a
 * handful of routes by hand and fell through to a generic
 * "Dashboard" for everything else — so any deep page showed no
 * indication of where the user actually was.
 *
 * Because it walks the same config the sidebar renders, a new
 * destination gets a correct breadcrumb for free.
 */
export function useBreadcrumbs(): Crumb[] {
  const { t } = useTranslation();
  const {
    pathname,
    isLeafActive,
    isGroupActive,
    isSettingsActive,
    inSettings,
  } = useActiveNav();

  const label = (key: string, fallback?: string) =>
    fallback ? t(key, fallback) : t(key);

  if (inSettings) {
    const spec = SETTINGS_SECTION_SPECS.find((s) => isSettingsActive(s.id));
    return [
      { label: t('nav.settings'), href: '/dashboard/settings' },
      ...(spec ? [{ label: t(spec.labelKey) }] : []),
    ];
  }

  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.kind === 'link' && isLeafActive(item)) {
        const crumbs: Crumb[] = [
          { label: label(item.labelKey, item.labelFallback) },
        ];
        // A deeper path than the nav item itself means we are on a
        // detail route (e.g. /patients/:id) — keep the parent
        // clickable so there is always a way back up.
        if (!matchesPath(pathname, item.href, true)) {
          crumbs[0].href = item.href;
          crumbs.push({ label: t('nav.details', 'Details') });
        }
        return crumbs;
      }

      if (item.kind === 'group' && isGroupActive(item)) {
        const child = item.children.find(
          (c): c is NavLeaf => c.kind === 'link' && isLeafActive(c),
        );
        const group = { label: label(item.labelKey, item.labelFallback) };
        if (!child) return [group];
        const childCrumb: Crumb = {
          label: label(child.labelKey, child.labelFallback),
        };
        // Deeper than the link itself (claims/:id, partners/:id): a detail
        // page, so its list is the parent and stays one tap away.
        if (!matchesPath(pathname, child.href, true)) {
          childCrumb.href = child.href;
          return [group, childCrumb, { label: t('nav.details', 'Details') }];
        }
        return [group, childCrumb];
      }
    }
  }

  // Pages reached from the top bar or the sidebar footer rather than the nav
  // list. They used to fall through to "Dashboard", which names no page.
  const offNav: Array<[prefix: string, labelKey: string]> = [
    ['/dashboard/chat', 'nav.chat'],
    ['/dashboard/notifications', 'nav.notifications'],
    ['/dashboard/feedback', 'feedback.title'],
  ];
  const match = offNav.find(([prefix]) => pathname.startsWith(prefix));
  if (match) {
    const [prefix, labelKey] = match;
    return pathname === prefix
      ? [{ label: t(labelKey) }]
      : [{ label: t(labelKey), href: prefix }, { label: t('nav.details') }];
  }

  return [{ label: t('nav.dashboard') }];
}
